import React, { useMemo } from 'react';
import { Calendar, momentLocalizer, Views } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { Task, CalendarEvent } from '../../types';

const localizer = momentLocalizer(moment);

interface AdminCalendarProps {
  tasks: Task[];
}

const AdminCalendar: React.FC<AdminCalendarProps> = ({ tasks }) => {
  const events: CalendarEvent[] = useMemo(() => {
    return tasks
      .filter(task => task.dueDate || task.startDate || task.endDate)
      .map(task => {
        const start = task.startDate ? new Date(task.startDate) : new Date(task.dueDate!);
        const end = task.endDate ? new Date(task.endDate) : 
                   task.dueDate ? new Date(task.dueDate) : 
                   new Date(start.getTime() + 24 * 60 * 60 * 1000); // Add 1 day if no end date

        return {
          id: task.id,
          title: task.title,
          start,
          end,
          resource: task,
          color: task.team?.color || '#3B82F6',
        };
      });
  }, [tasks]);

  const eventStyleGetter = (event: CalendarEvent) => {
    return {
      style: {
        backgroundColor: event.color,
        borderRadius: '4px',
        opacity: 0.8,
        color: 'white',
        border: '0px',
        display: 'block',
      },
    };
  };

  const CustomEvent = ({ event }: { event: CalendarEvent }) => {
    const task = event.resource;
    return (
      <div className="p-1">
        <div className="font-medium text-xs">{event.title}</div>
        <div className="text-xs opacity-75">
          {task.team?.name || 'No team'} • {task.priority}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Calendar View</h2>
      </div>

      <div className="card">
        <div style={{ height: '600px' }}>
          <Calendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            style={{ height: '100%' }}
            eventPropGetter={eventStyleGetter}
            components={{
              event: CustomEvent,
            }}
            views={[Views.MONTH, Views.WEEK, Views.DAY]}
            defaultView={Views.MONTH}
            popup
            showMultiDayTimes
            step={60}
            showAllEvents
          />
        </div>
      </div>

      {/* Legend */}
      <div className="card">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Legend</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-green-500 rounded"></div>
            <span className="text-sm text-gray-700">Low Priority</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-blue-500 rounded"></div>
            <span className="text-sm text-gray-700">Normal Priority</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-yellow-500 rounded"></div>
            <span className="text-sm text-gray-700">High Priority</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-red-500 rounded"></div>
            <span className="text-sm text-gray-700">Urgent Priority</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCalendar;
